import { head } from 'lodash';
import { ChartType } from '@opengov/reporting-charts';
import {
  IAccountSummary,
  IAccountSummaryChart,
  createAccountSummaryChart,
  IReportConfiguration,
  isRowDataSource,
  AccountSummaryChartValueType,
  MultiHierarchyAccountSummary,
  isMultiHierarchyAccountSummary,
  isMultiHierarchyCalculatedRowGroup
} from '@opengov/reporting-types';
import { BAR_LABEL_POSITION_MAP, HORIZONTAL_BAR_LABEL_POSITION_MAP } from './ChartSettingsForm/constants';

function isWildcard(str: string): boolean {
  return str === '*';
}

export function resetLineChart(chartConfig: IAccountSummaryChart): IAccountSummaryChart {
  chartConfig.stacking = 'none';
  chartConfig.barLabels = { enabled: false };
  return chartConfig;
}

function resetSeriesToNonWildcardOrDefault(series: string[][], defaultValue: string[][] = []): string[][] {
  const headSeries = head(series);
  return headSeries && !headSeries.some(isWildcard) ? [headSeries] : defaultValue;
}

/**
 * A y-axis series item of a single segment is a whole column group ("All X Columns") rather than one
 * addressable column value. Pie and gauge plot specific columns, so such an item must not survive a
 * chart-type switch: the backend expands it to one entry per column value, which desynchronizes the
 * authored and prepared series a gauge relies on to tell its value from its maximum.
 */
function isWholeColumnGroupSeriesItem(seriesItem: string[]): boolean {
  return seriesItem.length === 1;
}

function resetYAxisSeriesToNonWildcardOrDefault(series: string[][], defaultValue: string[][] = []): string[][] {
  const headSeries = head(series);
  return headSeries && !isWholeColumnGroupSeriesItem(headSeries) && !headSeries.some(isWildcard)
    ? [headSeries]
    : defaultValue;
}

export function resetPieChart(
  chartConfig: IAccountSummaryChart,
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary
): IAccountSummaryChart {
  if (chartConfig.xAxis.source === 'cols') {
    chartConfig.xAxis.series = resetSeriesToNonWildcardOrDefault(
      chartConfig.xAxis.series,
      getDefaultXAxisSeries(accountSummary, chartConfig.type)
    );
  } else {
    chartConfig.yAxis.series = resetYAxisSeriesToNonWildcardOrDefault(chartConfig.yAxis.series);
  }
  chartConfig.targets = [];
  chartConfig.barLabels = { enabled: false };
  chartConfig.yAxis.multipleAxis = {
    enabled: false,
    series: []
  };
  return chartConfig;
}

export function resetGaugeChart(
  chartConfig: IAccountSummaryChart,
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary
): IAccountSummaryChart {
  if (chartConfig.xAxis.source === 'rows') {
    chartConfig.xAxis.series = resetSeriesToNonWildcardOrDefault(
      chartConfig.xAxis.series,
      getDefaultXAxisSeriesForGauge(accountSummary)
    );
    // Filter before slicing so two valid columns are kept even when an unusable one comes first.
    const yAxisSlice = chartConfig.yAxis.series
      .filter((item) => !item.some(isWildcard) && !isWholeColumnGroupSeriesItem(item))
      .slice(0, 2);
    if (yAxisSlice.length == 1) {
      yAxisSlice.push(yAxisSlice[0]);
    }
    chartConfig.yAxis.series = yAxisSlice;
  } else {
    chartConfig.yAxis.series = resetYAxisSeriesToNonWildcardOrDefault(chartConfig.yAxis.series);
    const xAxisSlice = chartConfig.xAxis.series.slice(0, 2).filter((item) => !item.some(isWildcard));
    if (xAxisSlice.length == 1) {
      xAxisSlice.push(xAxisSlice[0]);
    } else if (xAxisSlice.length == 0) {
      // There may be no usable default: an MH report whose row groups are all calculated has no
      // chartable row group. Leave the selection empty rather than pushing `undefined` into it, which
      // would serialize as `[null, null]` and crash expandMhXAxisRowGroupWildcards on the backend.
      const defaultXAxisSeries = getDefaultXAxisSeriesForGauge(accountSummary);
      if (defaultXAxisSeries.length > 0) {
        xAxisSlice.push(defaultXAxisSeries[0], defaultXAxisSeries[0]);
      }
    }
    chartConfig.xAxis.series = xAxisSlice;
  }
  chartConfig.stacking = 'none';
  chartConfig.targets = [];
  chartConfig.barLabels = { enabled: false };
  chartConfig.yAxis.multipleAxis = {
    enabled: false,
    series: []
  };
  return chartConfig;
}

function resetBarChart(chartConfig: IAccountSummaryChart): IAccountSummaryChart {
  const { enabled: barLabelsEnabled, position: barLabelsPosition } = chartConfig.barLabels ?? {};
  chartConfig.barLabels = {
    enabled: !!barLabelsEnabled,
    position: BAR_LABEL_POSITION_MAP[barLabelsPosition ?? 'middle']
  };
  return chartConfig;
}

function resetHorizontalBarChart(chartConfig: IAccountSummaryChart): IAccountSummaryChart {
  const { enabled: barLabelsEnabled, position: barLabelsPosition } = chartConfig.barLabels ?? {};
  chartConfig.barLabels = {
    enabled: !!barLabelsEnabled,
    position: HORIZONTAL_BAR_LABEL_POSITION_MAP[barLabelsPosition ?? 'center']
  };
  chartConfig.yAxis.multipleAxis = {
    enabled: false,
    series: []
  };
  return chartConfig;
}

/**
 * Chart types that address their series through `seriesColors`, grouped by which report dimension
 * `xAxis.source` makes the series at a given setting. At the *same* setting the two families address
 * opposite dimensions: `cols` gives a bar/line its series from rows (`computeTransposedCategoryIds`
 * reads `xAxis.series`) but gives a pie its slices from columns (`_applySeriesTransposed` reads
 * `yAxis.series`), and `rows` is the mirror of that.
 *
 * `gauge` is deliberately absent. It colours through the scalar `gaugeValueColor` rather than
 * `seriesColors`, so it has no coloured element to keep and its Source must be left alone.
 */
const SERIES_DIMENSION_FAMILY: Partial<Record<NonNullable<IAccountSummaryChart['type']>, 'cartesian' | 'radial'>> = {
  bar: 'cartesian',
  horizontalBar: 'cartesian',
  line: 'cartesian',
  pie: 'radial',
  donut: 'radial'
};

const INVERTED_X_AXIS_SOURCE: Record<IAccountSummaryChart['xAxis']['source'], IAccountSummaryChart['xAxis']['source']> =
  {
    rows: 'cols',
    cols: 'rows'
  };

function getSeriesDimensionFamily(chartType: IAccountSummaryChart['type']) {
  return chartType ? SERIES_DIMENSION_FAMILY[chartType] : undefined;
}

/**
 * Handles `xAxis.source` when the chart type changes.
 * - Inverts it when the new type belongs to the other series-dimension family, so the element the
 *   user coloured stays the element the new chart addresses and its saved `seriesColors` key still
 *   resolves.
 * - Leaves it alone within a family, and for the gauge and the initial type selection.
 *
 * Returns `null` when no change is needed. Inverting twice restores the original value, so a
 * round trip through the other family is lossless.
 */
export function handleChartTypeChangeForXAxisSource(
  prevChartType: IAccountSummaryChart['type'],
  currentChartType: IAccountSummaryChart['type'],
  currentSource: IAccountSummaryChart['xAxis']['source']
): IAccountSummaryChart['xAxis']['source'] | null {
  const prevFamily = getSeriesDimensionFamily(prevChartType);
  const currentFamily = getSeriesDimensionFamily(currentChartType);
  const familiesAddressOppositeDimensions = !!prevFamily && !!currentFamily && prevFamily !== currentFamily;

  return familiesAddressOppositeDimensions ? INVERTED_X_AXIS_SOURCE[currentSource] : null;
}

export function switchChartType(
  chartConfig: IAccountSummaryChart,
  chartType: IAccountSummaryChart['type'],
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary
): IAccountSummaryChart {
  if (chartType === null) {
    throw new Error('Chart type cannot be null. Provide a supported chart type.');
  }

  // Before the type is overwritten, and before the per-type resets below read the source: they trim
  // whichever dimension the new chart does not plot, and the flip decides which one that is.
  const flippedSource = handleChartTypeChangeForXAxisSource(chartConfig.type, chartType, chartConfig.xAxis.source);
  if (flippedSource) {
    chartConfig.xAxis.source = flippedSource;
  }

  chartConfig.type = chartType;
  chartConfig.valueType = 'currency';

  switch (chartType) {
    case 'line':
      return resetLineChart(chartConfig);
    case 'pie':
    case 'donut':
      return resetPieChart(chartConfig, accountSummary);
    case 'gauge':
      return resetGaugeChart(chartConfig, accountSummary);
    case 'horizontalBar':
      return resetHorizontalBarChart(chartConfig);
    case 'bar':
    default:
      return resetBarChart(chartConfig);
  }
}

/**
 * A gauge plots one row selection, so it defaults to the first row group. MH calculated row groups
 * (e.g. "Calculation Row 1") are excluded: the chart backend returns no data for them, so defaulting
 * to one would render an empty gauge. Mirrors `getDefaultXAxisSeriesForMhPie`.
 */
function getDefaultXAxisSeriesForGauge(accountSummary: IAccountSummary | MultiHierarchyAccountSummary): string[][] {
  const firstRowGroup = head(accountSummary.rowGroups.filter((rg) => !isMultiHierarchyCalculatedRowGroup(rg)));
  return firstRowGroup?.id ? [[firstRowGroup.id]] : [];
}

function firstRowGroupHasChildValues(accountSummary: IAccountSummary | MultiHierarchyAccountSummary): boolean {
  const firstRowGroup = head(accountSummary.rowGroups);
  return firstRowGroup ? isRowDataSource(firstRowGroup) && firstRowGroup.grouping.length > 0 : false;
}

function getDefaultXAxisSeriesForMhPie(accountSummary: MultiHierarchyAccountSummary): string[][] {
  return accountSummary.rowGroups.filter((rg) => !isMultiHierarchyCalculatedRowGroup(rg)).map((rg) => [rg.id]);
}

function getDefaultXAxisSeries(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary,
  chartType: string | null
): string[][] {
  if (chartType === 'gauge') {
    return getDefaultXAxisSeriesForGauge(accountSummary);
  }

  if (chartType === 'pie' || chartType === 'donut') {
    if (isMultiHierarchyAccountSummary(accountSummary)) {
      return getDefaultXAxisSeriesForMhPie(accountSummary);
    }
    return getDefaultXAxisSeriesForGauge(accountSummary);
  }

  const series: string[][] = [];
  const firstRowGroup = accountSummary.rowGroups[0];
  const firstRowGroupId = firstRowGroupHasChildValues(accountSummary) ? firstRowGroup.id : undefined;
  if (firstRowGroupId) {
    series.push([firstRowGroupId, '*']);
  } else {
    series.push(['*']);
  }
  return series;
}

function getDefaultYAxisSeries(
  _accountSummary: IAccountSummary | MultiHierarchyAccountSummary,
  chartType: string | null
): string[][] {
  if (['gauge'].includes(chartType ?? '')) {
    return [];
  }
  return [['*']];
}

function getDefaultXAxisSource(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary,
  chartType: string | null
): 'rows' | 'cols' {
  if (chartType === 'gauge') {
    return 'rows';
  }
  if ((chartType === 'pie' || chartType === 'donut') && isMultiHierarchyAccountSummary(accountSummary)) {
    return 'rows';
  }
  return 'cols';
}

function getDefaultStacking(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary,
  chartType: string | null
): 'none' | 'normal' {
  if (chartType === 'bar' || chartType === 'horizontalBar') {
    return firstRowGroupHasChildValues(accountSummary) ? 'normal' : 'none';
  }
  return 'none';
}

function getDefaultValueType(): AccountSummaryChartValueType {
  return 'currency';
}

export function createChartConfigForChartType(
  accountSummary: IAccountSummary | MultiHierarchyAccountSummary,
  chartType: string | null
): IAccountSummaryChart {
  const defaultConfig = createAccountSummaryChart({
    xAxis: {
      source: getDefaultXAxisSource(accountSummary, chartType),
      series: getDefaultXAxisSeries(accountSummary, chartType)
    },
    yAxis: {
      series: getDefaultYAxisSeries(accountSummary, chartType),
      multipleAxis: {
        enabled: false,
        series: []
      }
    },
    stacking: getDefaultStacking(accountSummary, chartType),
    valueType: getDefaultValueType()
  });

  return switchChartType(defaultConfig, chartType as ChartType, accountSummary);
}

export function getChart(reportConfiguration?: IReportConfiguration): IAccountSummaryChart | undefined {
  return head(reportConfiguration?.accountSummary?.charts);
}

export enum VisualizationEvents {
  CreateVisualization = 'Reporting: Create New Account Summary Visualization',
  ChangeVisualizationType = 'Reporting: Change Account Summary Visualization Type',
  ChangeVisualizationSettings = 'Reporting: Update Account Summary Saved View with Visualization',
  DeleteVisualization = 'Reporting: Delete Account Summary Visualization from Saved View'
}
